import { v4 as uuidv4 } from 'uuid';
import { CommunicationChannel } from '../types/communications';
import {
    ActionType,
    CallApiConcatOperator,
    type ConversionTrigger,
    type DateTimeConfig,
    DifferenceOperator,
    type DurationTimeConfig,
    HoldBoundaryType,
    type HoldConfig,
    type JourneyBlocksMetadata,
    JourneyGroup,
    type JourneyNode,
    type JourneyNodesConfig,
    TimeConfigType,
    WAIT_FOR_WH_CACHE_BLOCK_ID,
} from '../types/journeys';
import assertUnreachable from './assertUnreachable';

export const journeyBlocks: JourneyBlocksMetadata[] = [
    {
        id: '1',
        title: 'Delay',
        description: undefined,
        group: JourneyGroup.DELAY,
        actions: [
            {
                actionType: ActionType.WAIT,
                config: {
                    type: TimeConfigType.DURATION,
                    allowEveryoneElse: false,
                },
            },
        ],
        allowedInExperiment: false,
    },
    {
        id: '2',
        title: 'Wait for action',
        description: undefined,
        group: JourneyGroup.DELAY,
        actions: [
            {
                actionType: ActionType.WAIT_UNTIL,
                config: {
                    allowEveryoneElse: true,
                },
            },
        ],
        allowedInExperiment: false,
    },
    {
        id: '3',
        title: 'Filter',
        description: undefined,
        group: JourneyGroup.FLOW_CONTROL,
        actions: [
            {
                actionType: ActionType.FILTER,
                config: {
                    allowEveryoneElse: true,
                },
            },
        ],
        allowedInExperiment: false,
    },
    {
        id: '4',
        title: 'Split',
        description: undefined,
        group: JourneyGroup.FLOW_CONTROL,
        actions: [
            {
                actionType: ActionType.SPLIT,
                config: {
                    allowEveryoneElse: true,
                },
            },
        ],
        allowedInExperiment: false,
    },
    {
        id: '5',
        title: 'Experiment',
        description: undefined,
        group: JourneyGroup.FLOW_CONTROL,
        actions: [
            {
                actionType: ActionType.EXPERIMENT,
                config: {
                    allowEveryoneElse: false,
                },
            },
        ],
        allowedInExperiment: false,
    },
    {
        id: '6',
        title: 'Send Email',
        description: undefined,
        group: JourneyGroup.ACTION,
        actions: [
            {
                actionType: ActionType.SEND_MESSAGE,
                config: {
                    channelType: CommunicationChannel.EMAIL,
                    allowEveryoneElse: false,
                },
            },
        ],
        allowedInExperiment: true,
    },
    {
        id: '7',
        title: 'Send SMS',
        description: undefined,
        group: JourneyGroup.ACTION,
        actions: [
            {
                actionType: ActionType.SEND_MESSAGE,
                config: {
                    channelType: CommunicationChannel.SMS,
                    allowEveryoneElse: false,
                },
            },
        ],
        allowedInExperiment: true,
    },
    {
        id: '8',
        title: 'Send WhatsApp',
        description: undefined,
        group: JourneyGroup.ACTION,
        actions: [
            {
                actionType: ActionType.SEND_MESSAGE,
                config: {
                    channelType: CommunicationChannel.WHATSAPP,
                    allowEveryoneElse: false,
                },
            },
        ],
        allowedInExperiment: true,
    },
    {
        id: '9',
        title: 'Send Push Notification',
        description: undefined,
        group: JourneyGroup.ACTION,
        actions: [
            {
                actionType: ActionType.SEND_MESSAGE,
                config: {
                    channelType: CommunicationChannel.PUSH,
                    allowEveryoneElse: false,
                },
            },
        ],
        allowedInExperiment: true,
    },
    {
        id: '10',
        title: 'Call API',
        description: undefined,
        group: JourneyGroup.ACTION,
        actions: [
            {
                actionType: ActionType.API,
                config: {
                    allowEveryoneElse: false,
                },
            },
        ],
        allowedInExperiment: true,
    },
    {
        id: '11',
        title: 'Wait till date',
        description: undefined,
        group: JourneyGroup.DELAY,
        actions: [
            {
                actionType: ActionType.WAIT,
                config: {
                    type: TimeConfigType.DATE_TIME,
                    allowEveryoneElse: true,
                },
            },
        ],
        allowedInExperiment: false,
    },
    {
        id: '12',
        title: 'Update trait',
        description: undefined,
        group: JourneyGroup.ACTION,
        actions: [
            {
                actionType: ActionType.UPDATE_TRAIT,
            },
        ],
        allowedInExperiment: true,
    },
    {
        id: '13',
        title: 'Add to journey',
        description: undefined,
        group: JourneyGroup.ACTION,
        actions: [
            {
                actionType: ActionType.SEND_TO_JOURNEY,
            },
        ],
        allowedInExperiment: true,
    },
];

export const isAllBranchDestinationsAreValid = (
    journey: JourneyNodesConfig,
): { isValid: boolean; invalidBranchesNodes: JourneyNode[] } => {
    const allNodeIds = new Set(journey.nodes.map((node) => node.id));

    const invalidBranchesNodes = journey.nodes.flatMap((node) => {
        const branches = node.branchConfig?.children?.branches ?? [];
        const hasInvalidBranch = branches.some(
            (branch) =>
                !branch.destination || !allNodeIds.has(branch.destination),
        );
        return hasInvalidBranch ? [node] : [];
    });

    return {
        isValid: invalidBranchesNodes.length === 0,
        invalidBranchesNodes,
    };
};
export const getBranchLimits = (actionType: ActionType) => {
    switch (actionType) {
        case ActionType.SPLIT:
            // Split: 0 or 1 default branch and 0 to many branches
            return {
                minDefaultBranches: 0,
                maxDefaultBranches: 1,
                minNonDefaultBranches: 0,
                maxNonDefaultBranches: Number.MAX_SAFE_INTEGER,
            };
        case ActionType.FILTER:
            // Filter: 0-1 default, 0-1 non-default
            return {
                minDefaultBranches: 0,
                maxDefaultBranches: 1,
                minNonDefaultBranches: 0,
                maxNonDefaultBranches: 1,
            };
        case ActionType.WAIT:
            // Wait: 0-1 non-default, 0-1 default
            return {
                minDefaultBranches: 0,
                maxDefaultBranches: 1,
                minNonDefaultBranches: 0,
                maxNonDefaultBranches: 1,
            };
        case ActionType.WAIT_UNTIL:
            // Wait until: 0-1 default, 0-1 non-default
            return {
                minDefaultBranches: 0,
                maxDefaultBranches: 1,
                minNonDefaultBranches: 0,
                maxNonDefaultBranches: 1,
            };
        case ActionType.EXPERIMENT:
            // Experiment: 0-1 non-default
            return {
                minDefaultBranches: 0,
                maxDefaultBranches: 0,
                minNonDefaultBranches: 0,
                maxNonDefaultBranches: 1,
            };
        case ActionType.SEND_MESSAGE:
            // Send message: 0-1 non-default
            return {
                minDefaultBranches: 0,
                maxDefaultBranches: 0,
                minNonDefaultBranches: 0,
                maxNonDefaultBranches: 1,
            };
        case ActionType.API:
            // API: 0-1 non-default
            return {
                minDefaultBranches: 0,
                maxDefaultBranches: 0,
                minNonDefaultBranches: 0,
                maxNonDefaultBranches: 1,
            };
        case ActionType.UPDATE_TRAIT:
            // Update trait: 0-1 non-default
            return {
                minDefaultBranches: 0,
                maxDefaultBranches: 0,
                minNonDefaultBranches: 0,
                maxNonDefaultBranches: 1,
            };
        case ActionType.SEND_TO_JOURNEY:
            // Send to journey: 0-1 non-default
            return {
                minDefaultBranches: 0,
                maxDefaultBranches: 0,
                minNonDefaultBranches: 0,
                maxNonDefaultBranches: 1,
            };
        case ActionType.SET:
            // Set: can have branches like other action types
            return {
                minDefaultBranches: 0,
                maxDefaultBranches: Number.MAX_SAFE_INTEGER,
                minNonDefaultBranches: 0,
                maxNonDefaultBranches: Number.MAX_SAFE_INTEGER,
            };
        default:
            // No branches for unknown action types
            return {
                minDefaultBranches: 0,
                maxDefaultBranches: 0,
                minNonDefaultBranches: 0,
                maxNonDefaultBranches: 0,
            };
    }
};

export const isNumberOfBranchesPerNodeValid = (
    journey: JourneyNodesConfig,
): { isValid: boolean; invalidNodes: JourneyNode[] } => {
    const invalidNodes: JourneyNode[] = [];

    journey.nodes.forEach((node) => {
        const branches = node.branchConfig?.children?.branches ?? [];
        const defaultBranches = branches.filter((branch) => branch.isDefault);
        const nonDefaultBranches = branches.filter(
            (branch) => !branch.isDefault,
        );

        const actionType = node.actions[0]?.type;
        if (!actionType) return;

        const limits = getBranchLimits(actionType);

        const isValid =
            defaultBranches.length >= limits.minDefaultBranches &&
            defaultBranches.length <= limits.maxDefaultBranches &&
            nonDefaultBranches.length >= limits.minNonDefaultBranches &&
            nonDefaultBranches.length <= limits.maxNonDefaultBranches;

        if (!isValid) {
            invalidNodes.push(node);
        }
    });

    return {
        isValid: invalidNodes.length === 0,
        invalidNodes,
    };
};

export const changeCampaignIdForSendMessageActions = (
    journeyNodesConfig: JourneyNodesConfig,
) => {
    const campaignIdMapping = new Map<string, string>();

    journeyNodesConfig.nodes.forEach((node) => {
        if (node.actions) {
            node.actions.forEach((action) => {
                if (
                    action.type === ActionType.SEND_MESSAGE &&
                    action.config.campaignId
                ) {
                    const oldCampaignId = action.config.campaignId;
                    if (!campaignIdMapping.has(oldCampaignId)) {
                        campaignIdMapping.set(oldCampaignId, uuidv4());
                    }
                }
            });
        }
    });
    const updatedNodes = journeyNodesConfig.nodes.map((node) => {
        if (node.actions) {
            const newActions = node.actions.map((action) => {
                if (action.type === ActionType.SEND_MESSAGE) {
                    const newCampaignId = action.config.campaignId
                        ? campaignIdMapping.get(action.config.campaignId) ||
                          uuidv4()
                        : uuidv4();
                    return {
                        ...action,
                        config: {
                            ...action.config,
                            campaignId: newCampaignId,
                        },
                    };
                }
                return action;
            });
            return { ...node, actions: newActions };
        }
        return node;
    });

    // If no old campaign IDs were found, return the updated nodes
    if (campaignIdMapping.size === 0) {
        return updatedNodes;
    }

    // Serialize the entire config to JSON string for regex replacement
    let configString = JSON.stringify({ nodes: updatedNodes });

    // Replace all occurrences of old campaign IDs (UUIDs) with new ones using regex
    campaignIdMapping.forEach((newId, oldId) => {
        // UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
        // Escape special regex characters (though UUIDs typically only have hyphens which aren't special)
        const escapedOldId = oldId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Create a regex that matches the complete UUID (ensuring we match the full UUID, not a substring)
        // Use word boundaries or ensure we match the complete UUID pattern
        const uuidPattern = new RegExp(escapedOldId, 'g');
        configString = configString.replace(uuidPattern, newId);
    });

    // Parse back to JourneyNodesConfig
    const updatedConfig: JourneyNodesConfig = JSON.parse(configString);

    return updatedConfig.nodes;
};

/**
 * this function checks if the conversion trigger is an in and out trigger
 * @param value
 * @returns boolean where true means the conversion trigger is an in and out trigger
 */
export function isAudienceInAndOutConversionTrigger(
    value: ConversionTrigger,
): boolean {
    return (
        value.associatedAudienceIds != null &&
        value.associatedAudienceIds.length > 0
    );
}

/**
 * this function is used to give the current node is warehouse wait node or not
 */
export const isWarehouseWaitNode = (node: JourneyNode): boolean =>
    node.metadata?.blockId === WAIT_FOR_WH_CACHE_BLOCK_ID;

/**
 * this function use to remove internal nodes from the journey nodes config
 */
export const removeInternalNodes = (
    journeyNodesConfig: JourneyNodesConfig,
    entryNodeId: string | undefined,
): {
    journeyNodesConfig: JourneyNodesConfig;
    entryNodeId: string | undefined;
} => {
    const waitForCacheNode = journeyNodesConfig.nodes.find((node) =>
        isWarehouseWaitNode(node),
    );
    if (waitForCacheNode) {
        const updatedNodes = journeyNodesConfig.nodes.filter(
            (node) => !isWarehouseWaitNode(node),
        );
        return {
            journeyNodesConfig: { ...journeyNodesConfig, nodes: updatedNodes },
            entryNodeId:
                waitForCacheNode.branchConfig?.children?.branches[0]
                    .destination ?? '',
        };
    }
    return {
        journeyNodesConfig,
        entryNodeId,
    };
};

/**
 * Prepares a string for JSONata evaluation by replacing the custom
 * concatenation operator (§) with the JSONata concatenation operator (&).
 * This allows users to include & in their actual content without conflicts.
 * @param {string} input - The input string with § as the concatenation operator
 * @returns {string} The string with & as the concatenation operator for JSONata
 */
export const prepareForJsonata = (input: string): string => {
    if (!input) return input;
    return input.replaceAll(CallApiConcatOperator, '&');
};

export const getDefaultTimeConfig = (
    timeConfigType: TimeConfigType,
): DurationTimeConfig | DateTimeConfig | HoldConfig => {
    switch (timeConfigType) {
        case TimeConfigType.DURATION:
            return {
                timeConfigType: TimeConfigType.DURATION,
                duration: 0,
            };
        case TimeConfigType.HOLD:
            return {
                timeConfigType: TimeConfigType.HOLD,
                boundaries: {
                    type: HoldBoundaryType.DAY_OF_WEEK,
                    allowedDays: [],
                    timeWindows: [],
                },
            };
        case TimeConfigType.DATE_TIME:
            return {
                timeConfigType: TimeConfigType.DATE_TIME,
                dateTime: { value: '' },
                timeDifference: {
                    value: 0,
                    operator: DifferenceOperator.ADD,
                },
            };
        default:
            return assertUnreachable(
                timeConfigType,
                `Unknown timeConfigType: ${timeConfigType}`,
            );
    }
};
